/**
 * PATH: packages/email-hub/src/feature/lib/quoteTrim.ts
 * FILE_ID: EMAILHUB-FEATURE-LIB-QUOTETRIM-001
 * PURPOSE: Split a single message's `body_html` into its NEW content and
 *          the QUOTED history it carries forward, so the stacked-thread
 *          reader (`EmailThread`) can render each message's own words and
 *          tuck the duplicated chain behind a "Show quoted text" toggle.
 * WHY:     Every reply/forward embeds the full prior conversation inline
 *          (Outlook `#divRplyFwdMsg`, Gmail `.gmail_quote`, Apple Mail
 *          "Begin forwarded message:", our own `.quotech-quote`). When we
 *          stack each message as its own block, showing the embedded chain
 *          in every block duplicates the whole thread N times. This util
 *          trims to the new bit — CONSERVATIVELY: it only cuts at the first
 *          recognised quote boundary and NEVER drops content (new + quoted
 *          always reconstitutes the original; if no marker is found the
 *          whole body is returned as `newHtml`).
 * FLOW:
 *   body_html → DOMParser → find earliest quote-boundary node →
 *   Range split (head = newHtml, tail = quotedHtml). No marker → all new.
 * RELATED:
 *   - components/EmailThread.tsx / ThreadMessage.tsx (consumers)
 *   - packages/ui/.../sanitizeHtml.ts (both halves are sanitised on render)
 */

export interface QuoteSplit {
  /** The message's own (new) content, with quoted history removed. */
  newHtml: string
  /** The trimmed quoted history (everything from the first boundary on).
   *  Empty string when no quote boundary was detected. */
  quotedHtml: string
}

/** CSS selectors that unambiguously mark the START of quoted history. */
const BLOCK_SELECTORS = [
  '.quotech-quote', // our own outbound collapsible-quote marker
  'blockquote', // Apple Mail / generic quoted block
  '.gmail_quote', // Gmail web + mobile
  '#appendonsend', // Outlook desktop "append on send" wrapper
  '#divRplyFwdMsg', // Outlook reply/forward header container
  '[id^="x_"]', // Outlook Web prefixes the ORIGINAL message ids with x_
]

/**
 * Text markers (matched against an element's trimmed text) that begin a
 * quoted block. Kept deliberately tight + anchored to the START so a user's
 * new prose can't accidentally trip them.
 */
function textMarksBoundary(text: string): boolean {
  if (!text) return false
  // Apple Mail forward divider.
  if (/^Begin forwarded message:/i.test(text)) return true
  // Outlook plain-text reply divider: "-----Original Message-----".
  if (/^-{2,}\s*Original Message\s*-{2,}/i.test(text)) return true
  // Gmail / generic attribution: "On <date>, <person> wrote:".
  if (/^On[\s\S]{0,300}?\bwrote:\s*$/i.test(text)) return true
  // Outlook header block: From: … (Sent|Date): … To: …
  if (
    /^From:\s/i.test(text) &&
    /(\n|\s)(Sent|Date):\s/i.test(text) &&
    /(\n|\s)To:\s/i.test(text)
  ) {
    return true
  }
  // Outlook underscore separator line.
  if (/^_{5,}\s*$/.test(text)) return true
  return false
}

function matchesBlockSelector(el: Element): boolean {
  for (const sel of BLOCK_SELECTORS) {
    try {
      if (el.matches(sel)) return true
    } catch {
      // Ignore malformed selectors in exotic engines.
    }
  }
  return false
}

/**
 * True when `el` is the TIGHTEST element whose text begins a quoted block —
 * i.e. it begins with a text marker but no child element does. This stops us
 * cutting at an outer wrapper that merely CONTAINS the quoted header (which
 * would swallow the user's new reply sitting above it).
 */
function matchesTextMarker(el: Element): boolean {
  const text = (el.textContent || '').trim()
  if (!textMarksBoundary(text)) return false
  for (const child of Array.from(el.children)) {
    if (textMarksBoundary((child.textContent || '').trim())) return false
  }
  return true
}

/** Outlook desktop emits `<hr>` immediately before the From:/Sent: header. */
function isHrBeforeHeader(el: Element): boolean {
  if (el.tagName !== 'HR') return false
  const next = el.nextElementSibling
  if (!next) return false
  return /^From:\s/i.test((next.textContent || '').trim())
}

/** Markers that identify a Microsoft Teams meeting card (Outlook embeds
 *  one when an invite is forwarded/replied). */
const TEAMS_BLOCK_MARKERS =
  /(microsoft teams meeting|join the meeting|teams\.microsoft\.com|meeting id:|join on your computer|join with a video conferencing device)/i

/** A line consisting solely of 5+ underscores — Outlook's lightweight
 *  divider. It precedes quoted history, but is ALSO the top/bottom border
 *  of a Teams meeting card, which is why it can't be trusted on its own. */
function isUnderscoreDivider(text: string): boolean {
  return /^_{5,}\s*$/.test(text)
}

/** Visible text that FOLLOWS `el` in document order, capped so we only
 *  inspect the block immediately after the divider — a Teams card sits
 *  right after its top border, and a real quoted reply's `From:` header
 *  does too, so a short window is enough to tell them apart. */
function followingText(
  el: Element, body: HTMLElement, doc: Document, maxChars = 800,
): string {
  try {
    const range = doc.createRange()
    range.setStartAfter(el)
    range.setEnd(body, body.childNodes.length)
    return range.toString().slice(0, maxChars)
  } catch {
    return ''
  }
}

/**
 * Earliest character offset of real quoted-history evidence in `text`, or
 * -1 if none. "Evidence" = the strong markers Outlook/Gmail/Apple emit at
 * the TOP of forwarded/quoted history: an "Original Message" rule, an
 * "On … wrote:" attribution, or a `From:` header paired with `Sent:`/`Date:`.
 * Used to decide whether a weak `_____` divider actually introduces quoted
 * history (vs being a Teams-card border or a trailing decorative line).
 */
function firstQuoteEvidenceIndex(text: string): number {
  const offsets: number[] = []
  const original = text.search(/-{2,}\s*original message/i)
  if (original >= 0) offsets.push(original)
  const wrote = text.search(/\bon\b[\s\S]{0,200}?\bwrote:/i)
  if (wrote >= 0) offsets.push(wrote)
  const fromMatch = /\bfrom:\s/i.exec(text)
  if (fromMatch && /\b(sent|date):\s/i.test(text)) offsets.push(fromMatch.index)
  return offsets.length ? Math.min(...offsets) : -1
}

/**
 * Decide whether a `_____` underscore divider is a genuine quoted-history
 * boundary.
 *
 * BUG-2026-06-21-EMAIL-TEAMS-CARD-HIDDEN (operator: *"same with teams link
 * which was sent"*): Outlook wraps the Teams "Join the meeting" card in the
 * very `_____` divider `textMarksBoundary` matches (top AND bottom border),
 * so the whole block was being trimmed into the quoted tail and hidden
 * behind "Show quoted text".
 *
 * The underscore line is a WEAK signal — quoted history, a Teams card border,
 * and a decorative footer rule all use it. So it only counts as a boundary
 * when real quoted-history evidence follows it AND that evidence is NOT
 * sitting behind a Teams card (otherwise we'd cut the Teams block off into
 * the quoted tail). Every STRONGER marker (From:/Sent: header, "Original
 * Message", gmail_quote, blockquote, #divRplyFwdMsg) stays authoritative and
 * never reaches this check.
 */
function isUnderscoreQuoteBoundary(
  el: Element, body: HTMLElement, doc: Document,
): boolean {
  const after = followingText(el, body, doc)
  const quoteIdx = firstQuoteEvidenceIndex(after)
  if (quoteIdx < 0) return false // no quoted history follows → not a boundary
  const teamsIdx = after.search(TEAMS_BLOCK_MARKERS)
  // A Teams card sits between this divider and the quoted history → this
  // divider is the Teams card's TOP border, not the quote boundary. Keep
  // walking; the divider AFTER the Teams card will be the real boundary.
  if (teamsIdx >= 0 && teamsIdx < quoteIdx) return false
  return true
}

/**
 * Walk the parsed body in document order and return the FIRST node that
 * marks the start of quoted history, or `null` if none is found. Document
 * order (TreeWalker) guarantees we cut at the earliest boundary, so the new
 * content above it is preserved intact.
 */
function findBoundary(body: HTMLElement, doc: Document): Node | null {
  const walker = doc.createTreeWalker(body, 0x1 /* NodeFilter.SHOW_ELEMENT */)
  let node: Node | null = walker.nextNode()
  while (node) {
    const el = node as Element
    if (matchesBlockSelector(el)) return el
    if (isHrBeforeHeader(el)) return el
    if (matchesTextMarker(el)) {
      // A bare `_____` divider only counts as a quote boundary when real
      // quoted history follows it (and isn't a Teams meeting card border)
      // — otherwise the Teams "Join the meeting" block, or a decorative
      // footer rule, would be hidden behind "Show quoted text". Every
      // stronger marker (From:/Sent:, Original Message, blockquote, …)
      // cuts unconditionally.
      const text = (el.textContent || '').trim()
      if (!isUnderscoreDivider(text) || isUnderscoreQuoteBoundary(el, body, doc)) {
        return el
      }
    }
    node = walker.nextNode()
  }
  return null
}

function fragmentToHtml(frag: DocumentFragment, doc: Document): string {
  const holder = doc.createElement('div')
  holder.appendChild(frag)
  return holder.innerHTML
}

/**
 * Split `html` into `{ newHtml, quotedHtml }`.
 *
 * Contract:
 *   - new + quoted always equals the original content (nothing dropped).
 *   - No boundary found → `{ newHtml: html, quotedHtml: '' }`.
 *   - Non-DOM / empty input → safe passthrough.
 */
export function splitNewVsQuoted(html: string | null | undefined): QuoteSplit {
  const raw = html ?? ''
  if (!raw.trim()) return { newHtml: raw, quotedHtml: '' }
  // Guard for non-DOM contexts (SSR / unit envs without a parser).
  if (typeof DOMParser === 'undefined') return { newHtml: raw, quotedHtml: '' }

  let doc: Document
  try {
    doc = new DOMParser().parseFromString(raw, 'text/html')
  } catch {
    return { newHtml: raw, quotedHtml: '' }
  }
  const body = doc.body
  if (!body) return { newHtml: raw, quotedHtml: '' }

  const boundary = findBoundary(body, doc)
  if (!boundary) return { newHtml: raw, quotedHtml: '' }

  try {
    const headRange = doc.createRange()
    headRange.selectNodeContents(body)
    headRange.setEndBefore(boundary)

    const tailRange = doc.createRange()
    tailRange.selectNodeContents(body)
    tailRange.setStartBefore(boundary)

    const newHtml = fragmentToHtml(headRange.cloneContents(), doc)
    const quotedHtml = fragmentToHtml(tailRange.cloneContents(), doc)

    // Guardrail: if the split produced nothing on both sides (shouldn't
    // happen) fall back to the original so we never blank a message.
    if (!newHtml.trim() && !quotedHtml.trim()) {
      return { newHtml: raw, quotedHtml: '' }
    }
    return { newHtml, quotedHtml }
  } catch {
    return { newHtml: raw, quotedHtml: '' }
  }
}
